/**
 * components/ThemeToggle.tsx
 * ------------------------------------------------------------------
 * Round 8 — three-way light / system / dark selector. Lives in the
 * Shell topbar, immediately before the notification bell.
 *
 * Three small icon-only buttons (sun / monitor / moon) inside a
 * single pill container. The active option gets a contrasting
 * background; the others stay subdued. State is owned by useTheme()
 * (lib/theme.ts) — this component is purely presentational +
 * delegates clicks to setMode().
 *
 * On mobile (md and below) we hide the segmented control and show
 * just the current-effective icon as a single button that cycles
 * through the three modes on tap. This keeps the topbar uncluttered
 * on narrow screens where the year/term selectors already compete
 * for space.
 * ------------------------------------------------------------------ */
import { Sun, Monitor, Moon } from 'lucide-react';
import { useTheme, type ThemeMode } from '@/lib/theme';

interface Option {
  mode: ThemeMode;
  Icon: typeof Sun;
  label: string;
}

const OPTIONS: Option[] = [
  { mode: 'light', Icon: Sun, label: 'Light' },
  { mode: 'system', Icon: Monitor, label: 'System' },
  { mode: 'dark', Icon: Moon, label: 'Dark' },
];

export default function ThemeToggle() {
  const { mode, setMode, effective } = useTheme();

  /** Mobile single-button: cycle through the modes in order. */
  function cycle() {
    const order: ThemeMode[] = ['light', 'system', 'dark'];
    const idx = order.indexOf(mode);
    const next = order[(idx + 1) % order.length];
    setMode(next);
  }

  /** The icon to show in mobile single-button mode. */
  const ActiveIcon = OPTIONS.find((o) => o.mode === mode)?.Icon ?? Monitor;

  return (
    <>
      {/* Desktop / tablet: segmented control */}
      <div
        role="radiogroup"
        aria-label="Theme"
        className="hidden md:inline-flex items-center rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-0.5"
      >
        {OPTIONS.map((opt) => {
          const Icon = opt.Icon;
          const active = mode === opt.mode;
          return (
            <button
              key={opt.mode}
              role="radio"
              aria-checked={active}
              aria-label={opt.label}
              title={opt.label}
              onClick={() => setMode(opt.mode)}
              className={`p-1.5 rounded-md transition-colors ${
                active
                  ? 'bg-primary text-white'
                  : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              <Icon size={14} strokeWidth={2} />
            </button>
          );
        })}
      </div>

      {/* Mobile: single icon button that cycles through modes */}
      <button
        onClick={cycle}
        aria-label={`Theme: ${mode} (currently ${effective}). Tap to change.`}
        title={`Theme: ${mode}`}
        className="md:hidden p-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
      >
        <ActiveIcon size={16} strokeWidth={2} />
      </button>
    </>
  );
}
